import { FontFamily } from "client/Api/common";
import { fontFamilyDefinition, supportedFontFamilies } from "client/Misc/fontTools";
import { never } from "client_legacy/ndaw/errors";
import {
	DOMOutputSpec,
	Mark,
	MarkSpec
} from "prosemirror-model";

export enum FontFamilyKind {
	Default = 0,
	Predefined = 1,
	Custom = 2
}

export type FontFamilyMarkAttributes = {
	kind: FontFamilyKind.Predefined;
	family: FontFamily;
} | {
	kind: FontFamilyKind.Custom;
	familyValue: string;
};

export function tryResolvePredefinedFontFamily(fontFamilyValue: string): TryResult<FontFamily> {
	if (!fontFamilyValue) {
		return { wasSuccessful: false };
	}
	const family: FontFamily | undefined = supportedFontFamilies
		.find(f => fontFamilyDefinition[f].parseRegExp
			.test(fontFamilyValue)
		);

	if (family) {
		return {
			wasSuccessful: true,
			result: family
		};
	}

	return {
		wasSuccessful: false
	};
}

export const fontFamily: MarkSpec = {
	attrs: {
		kind: { default: FontFamilyKind.Custom },
		family: { default: undefined },
		familyValue: { default: undefined }
	},
	parseDOM: [
		{
			style: "font-family",
			getAttrs: (styleValue): FontFamilyMarkAttributes | false => {
				if (typeof styleValue !== "string") {
					throw new Error("Expected style value at this point");
				}

				const predefinedFontFamilyResolution: TryResult<FontFamily> = tryResolvePredefinedFontFamily(styleValue);

				if (predefinedFontFamilyResolution.wasSuccessful) {
					return {
						kind: FontFamilyKind.Predefined,
						family: predefinedFontFamilyResolution.result
					};
				} else {
					return {
						kind: FontFamilyKind.Custom,
						familyValue: styleValue
					};
				}
			}
		}
	],
	toDOM: (node: Mark): DOMOutputSpec => {
		const attrs = node.attrs as FontFamilyMarkAttributes;

		let value: string;
		switch (attrs.kind) {
			case FontFamilyKind.Predefined:
				value = fontFamilyDefinition[attrs.family]
					.textRepresentation;
				break;
			case FontFamilyKind.Custom:
				value = attrs.familyValue;
				break;
			default:
				never(attrs);
		}

		return [
			"span",
			{
				style: `font-family: ${value};`
			},
			0
		];
	}
};